/**
 * ShowDatesPerformanceCard - Wrapper card for show dates table and totals
 * Provides unified "Show Dates & Performance Details" card matching legacy UX
 */

import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { Event as EventIcon } from '@mui/icons-material';
import type { HoldList, Hold } from 'api-client/models';
import { ConfirmedDatesTable } from './ConfirmedDatesTable';
import { TentativeHoldsTable } from './TentativeHoldsTable';
import { isHoldActionable } from '../../../utils/holdSelection';
import { HoldAggregateStats } from './HoldAggregateStats';

interface ShowDatesPerformanceCardProps {
  show: {
    id: string;
    status?: string;
  };
  holds: HoldList[];
  holdsLoading: boolean;
  selectedHoldIds: Set<string>;
  selectedHoldForDetails: HoldList | null;
  fullHoldDetails: Record<string, Hold>;
  onSelectHold: (holdId: string) => void;
  onSelectAllHolds: () => void;
  onSelectHoldForDetails: (hold: HoldList) => Promise<void>;
  onConfirmChallenge: () => void;
  onReleaseHolds: () => Promise<void>;
  onRefresh: () => Promise<void>;
  onOpenChallengeResponse: (hold: HoldList) => void;
  getConfirmChallengeButtonText: () => string;
}

export const ShowDatesPerformanceCard: React.FC<ShowDatesPerformanceCardProps> = ({
  show,
  holds,
  holdsLoading,
  selectedHoldIds,
  selectedHoldForDetails,
  fullHoldDetails,
  onSelectHold,
  onSelectAllHolds,
  onSelectHoldForDetails,
  onConfirmChallenge,
  onReleaseHolds,
  onRefresh,
  onOpenChallengeResponse,
  getConfirmChallengeButtonText,
}) => {
  const confirmedHolds = holds.filter((h) => h.status === 'confirmed');
  const tentativeHolds = show.status === 'confirmed' ? holds.filter(isHoldActionable) : holds;
  const confirmedDatesCount = new Set(
    confirmedHolds.map((h) => h.holdDate)
  ).size;

  return (
    <Card sx={{ overflow: 'visible' }}>
      <CardContent>
        {/* Header */}
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <EventIcon sx={{ mr: 1 }} />
          Show Dates & Performance Details
        </Typography>

        {/* Description Text */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: { xs: 'none', sm: 'block' } }}>
          {show.status === 'confirmed'
            ? `This show is confirmed across ${confirmedDatesCount} date${confirmedDatesCount !== 1 ? 's' : ''}. Select a date below to view or edit the details for that show date below.`
            : 'Select dates below to confirm, challenge, or release holds.'}
        </Typography>

        {/* Table Section */}
        {show.status === 'confirmed' && (
          <ConfirmedDatesTable
            holds={holds}
            holdsLoading={holdsLoading}
            selectedHoldForDetails={selectedHoldForDetails}
            fullHoldDetails={fullHoldDetails}
            onSelectHoldForDetails={onSelectHoldForDetails}
            onRefresh={onRefresh}
          />
        )}
        {tentativeHolds.length > 0 && (
          <Box sx={{ mt: show.status === 'confirmed' ? 2 : 0 }}>
            {show.status === 'confirmed' && <Typography variant="subtitle2" sx={{ mb: 1 }}>Other hold dates</Typography>}
            <TentativeHoldsTable
              holds={tentativeHolds}
              holdsLoading={holdsLoading}
              selectedHoldIds={selectedHoldIds}
              selectedHoldForDetails={selectedHoldForDetails}
              onSelectHold={onSelectHold}
              onSelectAllHolds={onSelectAllHolds}
              onSelectHoldForDetails={onSelectHoldForDetails}
              onConfirmChallenge={onConfirmChallenge}
              onReleaseHolds={onReleaseHolds}
              onRefresh={onRefresh}
              onOpenChallengeResponse={onOpenChallengeResponse}
              getConfirmChallengeButtonText={getConfirmChallengeButtonText}
            />
          </Box>
        )}

        {/* Show Totals - Only show for confirmed shows, wrapped in styled Card */}
        {confirmedHolds.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <HoldAggregateStats 
              holds={holds} 
              fullHoldDetails={fullHoldDetails}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
